import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { SexyModule } from '@qid/sexy';
import { PageActionComponent, SuperButtonComponent } from '@qid/super';
import { DocType } from '../../../sid';
import { DocumentSource } from '../../enum/document-source.enum';
import {
  IdVerificationOptions,
  IdentityVerificationSettings,
} from "../../interface/identity-verification-settings.interface";
import { CollectionTemplate } from "../../models/CollectionTemplate";

/** Which verification sources are available per document type. */
const DOCUMENT_SOURCES: Partial<Record<DocType, DocumentSource[]>> = {
  [DocType.ADHAR]: [DocumentSource.digilocker, DocumentSource.userUploaded],
  // [DocType.DRVLC]: [DocumentSource.digilocker, DocumentSource.userUploaded],
  // [DocType.VTRID]: [DocumentSource.digilocker, DocumentSource.userUploaded],
  [DocType.DRVLC]: [DocumentSource.userUploaded],
  [DocType.VTRID]: [DocumentSource.userUploaded],
  [DocType.PSPRT]: [DocumentSource.userUploaded],
};

@Component({
  selector: 'template-identity-verification-settings',
  templateUrl: './template-identity-verification-settings.component.html',
  styleUrls: ['./template-identity-verification-settings.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SexyModule,
    IonCheckbox,
    IonIcon,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonContent,
    IonFooter,
    PageActionComponent,
    SuperButtonComponent,
  ]
})

export class TemplateIdentityVerificationSettingsComponent implements OnInit {

  @Input() template: CollectionTemplate;
  @Input() pageMode = false;

  constructor(private modalController: ModalController) { }

  private sidNameForDocType(docType: DocType): string {
    switch (docType) {
      case DocType.ADHAR: return 'Aadhaar';
      case DocType.DRVLC: return 'Driving License';
      case DocType.VTRID: return 'Voter ID';
      case DocType.PSPRT: return 'Passport';
      default: return String(docType);
    }
  }

  private hydrateSidNames(s: IdentityVerificationSettings): void {
    const hydrateList = (opts?: IdVerificationOptions[]) => {
      if (!opts) return;
      for (const opt of opts) {
        if (!opt) continue;
        if (!opt.sidName && opt.sidKey) {
          opt.sidName = this.sidNameForDocType(opt.sidKey as DocType);
        }
      }
    };
    hydrateList(s.indianDocumentOptions);
    hydrateList(s.otherCountryDocumentOptions);
  }

  ngOnInit(): void {
    if (!this.template) return;
    if (!this.template.identityVerificationSettings) {
      this.template.identityVerificationSettings = {
        captureCompleteDocument: false,
        indianDocumentOptions: [
          { sidKey: DocType.ADHAR, sidName: this.sidNameForDocType(DocType.ADHAR), acceptedSources: [DocumentSource.aadhaarApp, DocumentSource.digilocker, DocumentSource.userUploaded] },
        ],
        otherCountryDocumentOptions: [
          { sidKey: DocType.PSPRT, sidName: this.sidNameForDocType(DocType.PSPRT), acceptedSources: [DocumentSource.userUploaded] },
        ],
      };
    }
    const s = this.template.identityVerificationSettings;
    if (!s.indianDocumentOptions || s.indianDocumentOptions.length === 0) {
      s.indianDocumentOptions = [
        { sidKey: DocType.ADHAR, sidName: this.sidNameForDocType(DocType.ADHAR), acceptedSources: [DocumentSource.aadhaarApp, DocumentSource.digilocker, DocumentSource.userUploaded] },
      ];
    }
    if (!s.otherCountryDocumentOptions || s.otherCountryDocumentOptions.length === 0) {
      s.otherCountryDocumentOptions = [
        { sidKey: DocType.PSPRT, sidName: this.sidNameForDocType(DocType.PSPRT), acceptedSources: [DocumentSource.userUploaded] },
      ];
    }
    this.hydrateSidNames(s);
  }

  private getIndianOptions(): IdVerificationOptions[] {
    return this.template?.identityVerificationSettings?.indianDocumentOptions ?? [];
  }

  private getOtherCountryOptions(): IdVerificationOptions[] {
    return this.template?.identityVerificationSettings?.otherCountryDocumentOptions ?? [];
  }

  private ensureOption(s: IdentityVerificationSettings, docType: DocType, isIndian: boolean): IdVerificationOptions {
    const list = isIndian ? s.indianDocumentOptions! : s.otherCountryDocumentOptions!;
    let opt = list.find((o) => o.sidKey === docType);
    if (!opt) {
      opt = { sidKey: docType, sidName: this.sidNameForDocType(docType), acceptedSources: [...(DOCUMENT_SOURCES[docType] ?? [])] };
      list.push(opt);
    }
    if (!opt.acceptedSources) opt.acceptedSources = [];
    if (!opt.sidName) opt.sidName = this.sidNameForDocType(docType);
    return opt;
  }

  isDocumentAccepted(docType: DocType): boolean {
    if (docType === DocType.PSPRT) return true;
    return this.getIndianOptions().some((o) => o.sidKey === docType);
  }

  setDocumentAccepted(docType: DocType, accepted: boolean): void {

    const s = this.template?.identityVerificationSettings;
    if (!s) return;
    if (!s.indianDocumentOptions) s.indianDocumentOptions = [];
    const list = s.indianDocumentOptions;
    const idx = list.findIndex((o) => o.sidKey === docType);
    if (accepted && idx < 0) {
      list.push({ sidKey: docType, sidName: this.sidNameForDocType(docType), acceptedSources: [...(DOCUMENT_SOURCES[docType] ?? [])] });
    } else if (!accepted && idx >= 0) {
      list.splice(idx, 1);
    }
  }

  isDocumentSelectionDisabled(docType: DocType): boolean {
    return docType === DocType.PSPRT;
  }

  getSourcesForDocument(docType: DocType): DocumentSource[] {
    return DOCUMENT_SOURCES[docType] ?? [];
  }

  isSourceSelected(docType: DocType, source: DocumentSource): boolean {
    const isIndian = docType !== DocType.PSPRT;
    const list = isIndian ? this.getIndianOptions() : this.getOtherCountryOptions();
    const opt = list.find((o) => o.sidKey === docType);
    return opt?.acceptedSources?.includes(source) ?? false;
  }

  toggleSource(docType: DocType, source: DocumentSource): void {
    if (!this.canDeselectSource(docType, source)) return;
    const s = this.template?.identityVerificationSettings;
    if (!s) return;
    const isIndian = docType !== DocType.PSPRT;
    if (isIndian && !s.indianDocumentOptions) s.indianDocumentOptions = [];
    if (!isIndian && !s.otherCountryDocumentOptions) s.otherCountryDocumentOptions = [];
    const opt = this.ensureOption(s, docType, isIndian);
    const idx = opt.acceptedSources.indexOf(source);
    if (idx >= 0) {
      if (opt.acceptedSources.length <= 1) return;
      opt.acceptedSources.splice(idx, 1);
    } else {
      opt.acceptedSources.push(source);
    }
  }

  canDeselectSource(docType: DocType, source: DocumentSource): boolean {
    const sources = DOCUMENT_SOURCES[docType] ?? [];
    return sources.length > 1;
  }

  sourceLabel(source: DocumentSource): string {
    switch (source) {
      case DocumentSource.aadhaarApp: return 'Aadhaar App';
      case DocumentSource.digilocker: return 'Digilocker';
      case DocumentSource.userUploaded: return 'User uploaded Photo';
      default: return String(source);
    }
  }

  readonly documentRows: { docType: DocType; label: string }[] = [
    { docType: DocType.ADHAR, label: 'Aadhaar' },
    { docType: DocType.DRVLC, label: 'Driving License' },
    { docType: DocType.VTRID, label: 'Voter ID' },
    { docType: DocType.PSPRT, label: 'Passport' },
  ];
  readonly passportRow = { docType: DocType.PSPRT, label: 'Passport' };

  save(): void {
    this.modalController.dismiss({
      identityVerificationSettings: this.template?.identityVerificationSettings,
    });
  }

  close(): void {
    this.modalController.dismiss();
  }
}

